import React, { useState, lazy, Suspense } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { X, MapPin } from 'lucide-react';
import toast from 'react-hot-toast';
import AddressAutocomplete from './AddressAutocomplete';
import SimilarClientWarning from './SimilarClientWarning';
import api from '../api';

const LocationPicker = lazy(() => import('./LocationPicker'));

/**
 * The one and only add/edit client form.
 *
 * It used to exist twice — a full version on the Clients page and a cut-down
 * "quick add" on New Parcel that silently dropped business name, first/surname,
 * VAT number, payment terms, "pays for parcels they send" and the bank
 * statement keywords. A client added while booking a parcel came out different
 * from the same client added on the Clients page, and the missing billing
 * fields had to be filled in later by hand.
 *
 * The panel is capped at 90vh with its own scrollbar, so the header and the
 * save button stay put on a laptop screen instead of the form running off the
 * bottom of the page.
 *
 * The root is a plain <div>, NOT a <form>. New Parcel opens this from inside its
 * own <form>, and a nested form is both invalid HTML and actively harmful: the
 * submit event bubbles, so pressing "Add Client" also ran the parcel form's
 * validation and fired a red "Select a receiver" at the same moment as the green
 * "added". The client had saved; the screen said it had not. Saving goes through
 * a type="button" onClick instead, so no submit event exists to escape.
 *
 * Enter no longer submits, and must not be re-added — the Address field is an
 * AddressAutocomplete where Enter picks a suggestion.
 *
 * onSaved is called with the saved client, so New Parcel can select it straight
 * away as the sender or receiver.
 */
export default function ClientFormModal({ existing, title, onClose, onSaved }) {
  const qc = useQueryClient();
  const [form, setForm] = useState(existing || {
    name: '', first_name: '', last_name: '', business_name: '',
    email: '', phone: '', address: '', city: '', postal_code: '', notes: '',
    role: 'both', latitude: '', longitude: '',
    vat_number: '', payment_terms: 'cod', pays_for_sent: false, match_keywords: '',
    notify_email: false, notify_whatsapp: false, notify_telegram: false,
  });
  const [showMap, setShowMap] = useState(false);
  const [loading, setLoading] = useState(false);
  const [similar, setSimilar] = useState(null);   // matches found before creating; null = not checked
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  // The display name auto-fills from business or first+surname — but only while
  // the user hasn't typed their own, so a custom label is never overwritten.
  const derive = (f) => (f.business_name || '').trim()
    || [f.first_name, f.last_name].map((s) => (s || '').trim()).filter(Boolean).join(' ');
  const setNamePart = (k) => (e) => setForm((f) => {
    const nf = { ...f, [k]: e.target.value };
    if (!f.name || f.name === derive(f)) nf.name = derive(nf);
    return nf;
  });

  const submit = async (force = false) => {
    if (!form.name) return toast.error('Name is required');
    setLoading(true);
    try {
      // New client: look for someone who already exists under another spelling
      // or with the same phone/email/address, and let the office decide.
      if (!existing && !force) {
        const { data: matches } = await api.get('/clients/similar', {
          params: { name: form.name, phone: form.phone, email: form.email, address: form.address },
        });
        if (matches.length) { setSimilar(matches); setLoading(false); return; }
      }
      const { data } = existing
        ? await api.patch(`/clients/${existing.id}`, form)
        : await api.post('/clients', { ...form, role: 'both' });

      // Refresh every list that shows clients, from here rather than in each
      // caller. Adding from New Parcel used to invalidate only the parcel's own
      // search, so the Clients page kept serving its cached list and the new
      // client looked like it had never saved.
      qc.invalidateQueries({ queryKey: ['clients'] });
      qc.invalidateQueries({ queryKey: ['entity-search'] });

      toast.success(existing ? 'Client updated' : `${form.name} added`);
      onSaved(data);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save client');
    } finally { setLoading(false); }
  };

  const heading = title || (existing ? 'Edit Client' : 'Add Client');

  if (similar) {
    return (
      <div className="fixed inset-0 z-50 flex items-start overflow-y-auto p-4 bg-black/50">
        <div className="bg-white rounded-xl shadow-xl w-full max-w-lg m-auto my-4 p-5 space-y-3">
          <div className="flex items-center gap-2">
            <span className="text-2xl">⚠️</span>
            <div>
              <h3 className="font-bold text-gray-800">This client may already exist</h3>
              <p className="text-xs text-gray-500">{similar.length === 1 ? 'One client matches' : `${similar.length} clients match`} what you typed for <strong>{form.name}</strong>. Open the existing one, or create a new client anyway.</p>
            </div>
          </div>
          <div className="border rounded-lg divide-y max-h-80 overflow-auto">
            {similar.map((c) => (
              <div key={c.id} className="p-3 flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-semibold text-gray-800 text-sm">{c.name}{c.business_name && c.business_name !== c.name ? <span className="text-gray-400 font-normal"> · {c.business_name}</span> : null}</p>
                  <p className="text-xs text-gray-500 truncate">{[c.phone, c.email, c.address, c.city].filter(Boolean).join(' · ') || 'no contact details'}</p>
                  <p className="text-[11px] mt-1">
                    {c.reasons.map((r) => <span key={r} className="inline-block mr-1 px-1.5 py-0.5 rounded bg-amber-100 text-amber-800">{r}</span>)}
                    <span className="text-gray-400">{c.parcels} parcel{c.parcels === 1 ? '' : 's'} · since {new Date(c.created_at).toLocaleDateString('en-ZA')}</span>
                  </p>
                </div>
                <a href={`/clients/${c.id}`} target="_blank" rel="noreferrer"
                  className="flex-shrink-0 text-xs font-semibold text-orange-600 border border-orange-200 hover:bg-orange-50 rounded-lg px-3 py-1.5 whitespace-nowrap">
                  Open &amp; edit
                </a>
              </div>
            ))}
          </div>
          <div className="flex flex-wrap justify-end gap-2 pt-1">
            <button onClick={() => setSimilar(null)} className="px-4 py-2 rounded-lg text-sm border text-gray-600">Back to the form</button>
            <button onClick={onClose} className="px-4 py-2 rounded-lg text-sm border text-gray-600">Cancel — use the existing client</button>
            <button onClick={() => { setSimilar(null); submit(true); }}
              className="px-4 py-2 rounded-lg text-sm font-semibold bg-orange-500 hover:bg-orange-600 text-white">
              Create new client anyway
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-start p-4 bg-black/50 overflow-y-auto">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] flex flex-col m-auto my-4">

        {/* Header — stays visible while the body scrolls */}
        <div className="flex items-center justify-between px-6 py-4 border-b flex-shrink-0">
          <h2 className="font-bold text-gray-800">{heading}</h2>
          <button type="button" onClick={onClose}><X size={20} className="text-gray-400" /></button>
        </div>

        {/* Scrolling body */}
        <div className="px-6 py-4 space-y-3 overflow-y-auto">
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <label className="block text-xs font-medium text-gray-600 mb-1">Business name</label>
              <input type="text" value={form.business_name || ''} onChange={setNamePart('business_name')}
                placeholder="Company / trading name (if any)"
                className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">First name</label>
              <input type="text" value={form.first_name || ''} onChange={setNamePart('first_name')}
                className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Surname</label>
              <input type="text" value={form.last_name || ''} onChange={setNamePart('last_name')}
                className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400" />
            </div>
            <div className="col-span-2">
              <label className="block text-xs font-medium text-gray-600 mb-1">Display name *</label>
              <input type="text" value={form.name || ''} onChange={set('name')} autoFocus
                placeholder="Shown on parcels, invoices & statements"
                className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400" />
              <p className="text-[11px] text-gray-400 mt-0.5">Auto-filled from business or first + surname — edit if you want a different label.</p>
              {!existing && <SimilarClientWarning name={form.name} className="mt-2" />}
            </div>
            {[['email', 'Email'], ['phone', 'Phone'], ['address', 'Address'], ['city', 'City']].map(([k, lbl]) => (
              <div key={k} className={k === 'address' ? 'col-span-2' : ''}>
                <label className="block text-xs font-medium text-gray-600 mb-1">{lbl}</label>
                {k === 'address' ? (
                  <AddressAutocomplete
                    value={form.address}
                    town={form.city}
                    onChange={(v) => setForm((f) => ({ ...f, address: v }))}
                    onSelect={({ address, city, postal_code, latitude, longitude }) =>
                      setForm((f) => ({
                        ...f, address, city: city || f.city,
                        postal_code: postal_code || f.postal_code, latitude, longitude,
                      }))}
                    placeholder="Start typing an address…"
                    className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400"
                  />
                ) : (
                  <input type="text" value={form[k] || ''} onChange={set(k)}
                    className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400" />
                )}
              </div>
            ))}
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Notes</label>
            <textarea rows={2} value={form.notes || ''} onChange={set('notes')}
              className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400" />
          </div>

          {/* Billing */}
          <div className="border-t pt-3">
            <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Billing</p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">VAT number (optional)</label>
                <input type="text" value={form.vat_number || ''} onChange={set('vat_number')}
                  placeholder="Shown on their tax invoices"
                  className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Payment terms</label>
                <select value={form.payment_terms || 'cod'}
                  onChange={(e) => setForm((f) => ({ ...f, payment_terms: e.target.value }))}
                  className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400">
                  <option value="cod">COD — pays immediately</option>
                  <option value="7">7 days</option>
                  <option value="30">30 days</option>
                </select>
              </div>
            </div>
            <label className="flex items-start gap-2 mt-2 cursor-pointer">
              <input type="checkbox" checked={!!form.pays_for_sent}
                onChange={(e) => setForm((f) => ({ ...f, pays_for_sent: e.target.checked }))}
                className="mt-0.5 accent-orange-500" />
              <span className="text-sm text-gray-700">
                This client <strong>pays for parcels they send</strong>
                <span className="block text-xs text-gray-400">Normally the receiver pays. Tick this if this client (as a sender) has an arrangement to pay for parcels they send out.</span>
              </span>
            </label>

            <div className="mt-3">
              <label className="block text-xs font-medium text-gray-500 mb-1">Bank statement keywords</label>
              <input type="text"
                value={Array.isArray(form.match_keywords) ? form.match_keywords.join(', ') : (form.match_keywords || '')}
                onChange={(e) => setForm((f) => ({ ...f, match_keywords: e.target.value }))}
                placeholder="e.g. It Direct, ITD, Overstrand"
                className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400" />
              <p className="text-xs text-gray-400 mt-1">
                Separate with commas. When you import a Capitec statement, a payment whose
                description contains one of these words is matched to this client. Use whatever
                actually shows on the statement — their trading name, or the reference they always use.
              </p>
              <p className="text-xs text-amber-600 mt-1">
                Keep them distinctive. Short ones like <em>ITD</em> or <em>OK</em> also turn up in other
                people&apos;s references and will match the wrong client.
              </p>
            </div>
          </div>

          {/* Notifications — every channel stays OFF unless the office ticks it,
              so an imported or brand-new client is never messaged by accident. */}
          <div className="border-t pt-3">
            <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Notification Channels</p>
            <div className="space-y-2">
              {[
                { key: 'notify_email',    label: 'Email',    desc: 'Auto-send email when parcel status changes' },
                { key: 'notify_whatsapp', label: 'WhatsApp', desc: 'Staff sends manual WhatsApp using templates' },
                { key: 'notify_telegram', label: 'Telegram', desc: 'Send Telegram alert to group on status change' },
              ].map(({ key, label, desc }) => (
                <label key={key} className="flex items-start gap-3 cursor-pointer group">
                  <input type="checkbox" checked={!!form[key]}
                    onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.checked }))}
                    className="mt-0.5 accent-orange-500 w-4 h-4 flex-shrink-0" />
                  <div>
                    <span className="text-sm font-medium text-gray-700">{label}</span>
                    <p className="text-xs text-gray-400">{desc}</p>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {/* Map pin */}
          <div className="border-t pt-3">
            <button type="button" onClick={() => setShowMap((s) => !s)}
              className="flex items-center gap-1 text-xs text-orange-500 hover:underline">
              <MapPin size={13} />
              {showMap ? 'Hide map' : (form.latitude ? 'Edit location on map' : 'Pin delivery location on map')}
            </button>
            {form.latitude && !showMap && (
              <p className="text-xs text-gray-400 mt-1">
                Pinned: {parseFloat(form.latitude).toFixed(5)}, {parseFloat(form.longitude).toFixed(5)}
              </p>
            )}
            {showMap && (
              <div className="mt-2">
                <Suspense fallback={<div className="h-56 bg-gray-100 rounded-lg flex items-center justify-center text-gray-400 text-sm">Loading map...</div>}>
                  <LocationPicker
                    lat={form.latitude} lng={form.longitude}
                    defaultArea="gansbaai"
                    onChange={({ lat, lng }) => {
                      setForm((f) => ({ ...f, latitude: lat, longitude: lng }));
                      // A pin says which town it is in — fill the town if nobody typed one
                      api.get('/geo/suburb', { params: { lat, lng } })
                        .then((r) => { if (r.data?.town) setForm((f) => (f.city ? f : { ...f, city: r.data.town })); })
                        .catch(() => {});
                    }}
                  />
                </Suspense>
              </div>
            )}
          </div>
        </div>

        {/* Footer — the save button is always reachable, whatever the screen height */}
        <div className="px-6 py-4 border-t flex-shrink-0">
          <button type="button" onClick={() => submit(false)} disabled={loading}
            className="w-full bg-orange-500 hover:bg-orange-600 text-white py-2 rounded-lg text-sm font-medium disabled:opacity-50">
            {loading ? 'Saving...' : (existing ? 'Save Changes' : 'Add Client')}
          </button>
        </div>
      </div>
    </div>
  );
}
